/**
 * The generation layer as a screen sees it.
 *
 * Everything here is additive. The deterministic payload — score, pillars,
 * amounts, ranked recommendations — is already on screen and already complete;
 * this only answers "is prose coming, and did it arrive". So the two states it
 * produces never block, never spin, and never take the error treatment:
 *
 *   analysisPending   prose is being written; every slot shows its copyKey
 *   analysisFailed    prose is not coming; the screen is identical to ready
 *
 * `unavailable` — no API key configured — folds into analysisFailed on
 * purpose, per flows.json. It is the expected steady state of a deploy that
 * opted out, it is never retried, and because analysisFailed renders exactly
 * as ready, the user never learns there was a layer they did not get.
 *
 * Polling follows analysis-status.schema.json x-polling and nothing else.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { AnalysisStatus } from '@/generated/types'
import { api } from './api'
import { bridge } from './bridge'

/** x-polling: 2s for the first 30s, then 5s, giving up after 90s. */
const FAST_INTERVAL_MS = 2_000
const SLOW_INTERVAL_MS = 5_000
const FAST_WINDOW_MS = 30_000
const GIVE_UP_MS = 90_000

export type AnalysisState = 'analysisPending' | 'analysisFailed' | null

export interface AnalysisGate {
  /**
   * The overlay state, or null when generation has landed and the screen is
   * simply `ready`. A screen resolves its own states first — loading, error,
   * empty, insufficientData all win over this, because they describe whether
   * the screen has data and this only describes whether it has prose.
   */
  state: AnalysisState
  status: AnalysisStatus | null
  /**
   * Whether a retry would be worth offering. Only healthScore and
   * recommendationDetail act on it; every other screen stays silent.
   */
  retryable: boolean
  /** Ask the Server to generate again, then resume polling. */
  retry: (target: { target: 'score' | 'recommendation'; id?: string }) => void
}

function intervalFor(elapsedMs: number): number {
  return elapsedMs < FAST_WINDOW_MS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS
}

/**
 * Per-unit generation state, as carried on the payload the screen already
 * fetched — `healthScore.narrativeState`, `progress.narrativeState`,
 * `recommendation.proseState`. Reading it costs no request, which is why every
 * screen starts here and only polls when this says something is in flight.
 */
export type UnitState = 'unavailable' | 'pending' | 'ready' | 'partial' | 'failed'

/**
 * A unit's state as the screen state flows.json declares, or null for "ready,
 * nothing to say".
 *
 * `undefined` maps to analysisFailed on purpose. Both the schema default and
 * the deploys that will actually run tonight — no API key, or a Server that has
 * not shipped the field — land here, and analysisFailed renders EXACTLY as
 * ready. So the absent case is the silent case, not the broken one.
 */
/**
 * One state for a screen whose only prose is the recommendation cards on it —
 * **cashFlow** and **categoryDetail**, which have no narrative of their own but
 * do list `recommendationCard`s and so declare `generatedText` in
 * screens.json. Anything still being written keeps the screen in
 * analysisPending; anything already written makes it ready.
 */
export function aggregateUnitState(states: (UnitState | undefined | null)[]): UnitState | undefined {
  if (states.some((s) => s === 'pending')) return 'pending'
  if (states.some((s) => s === 'ready' || s === 'partial')) return 'ready'
  return states.find((s) => s === 'failed') ?? undefined
}

export function unitAnalysisState(state: UnitState | undefined | null): AnalysisState {
  if (state === 'pending') return 'analysisPending'
  if (state === 'ready' || state === 'partial') return null
  return 'analysisFailed'
}

export interface UseAnalysisStatusOptions {
  /**
   * False while the screen has no data yet — there is nothing for prose to
   * attach to, and x-polling says poll only while something is in flight.
   */
  enabled?: boolean
  /**
   * Called when generation reaches ready or partial, so the screen can refetch
   * the payload that now carries prose. Not called on failure: the screen is
   * already showing everything it is going to show.
   */
  onGenerated?: () => void
}

/**
 * Why polling stopped without prose arriving. The two are NOT interchangeable:
 * `timeout` means generation was genuinely in flight and ran long, so asking
 * again is reasonable. `unreadable` means we never got an answer at all — the
 * endpoint 404s on a Server that has not shipped it, or the network is down —
 * and offering a retry there would render a control that cannot work. A failed
 * probe lands in the same state as an opted-out deploy: silently `ready`.
 */
type Stopped = 'timeout' | 'unreadable' | null

export function useAnalysisStatus({ enabled = true, onGenerated }: UseAnalysisStatusOptions = {}): AnalysisGate {
  const [status, setStatus] = useState<AnalysisStatus | null>(null)
  const [stopped, setStopped] = useState<Stopped>(null)
  const [nonce, setNonce] = useState(0)

  const onGeneratedRef = useRef(onGenerated)
  onGeneratedRef.current = onGenerated

  useEffect(() => {
    if (!enabled) return

    const controller = new AbortController()
    const startedAt = Date.now()
    let timer: ReturnType<typeof setTimeout> | undefined
    let live = true

    const poll = async (): Promise<void> => {
      let next: AnalysisStatus
      try {
        next = await api.analysis.status()
      } catch {
        // A status read that fails is not an error the user should see: the
        // screen is complete without it. Treat it as "no prose coming", and as
        // NOT retryable — we have no evidence anything is in flight to retry.
        if (live) setStopped('unreadable')
        return
      }
      if (!live || controller.signal.aborted) return
      setStatus(next)

      const generation = next.generation.state
      if (generation === 'ready' || generation === 'partial') {
        onGeneratedRef.current?.()
        return
      }
      if (generation !== 'pending') return // unavailable | failed — terminal

      const elapsed = Date.now() - startedAt
      if (elapsed >= GIVE_UP_MS) {
        bridge.log('analysis: generation still pending after 90s — entering analysisFailed')
        setStopped('timeout')
        return
      }
      timer = setTimeout(() => void poll(), intervalFor(elapsed))
    }

    void poll()

    return () => {
      live = false
      controller.abort()
      if (timer) clearTimeout(timer)
    }
  }, [enabled, nonce])

  const retry = useCallback((target: { target: 'score' | 'recommendation'; id?: string }) => {
    setStopped(null)
    setStatus(null)
    void api.analysis
      .explain(target)
      // 202 means queued, not done. Restarting the poll is the whole response.
      .catch(() => setStopped('unreadable'))
      .finally(() => setNonce((n) => n + 1))
  }, [])

  const generation = status?.generation
  const state: AnalysisState = stopped
    ? 'analysisFailed'
    : !generation
      ? null // status not read yet; the screen's own loading state covers it
      : generation.state === 'pending'
        ? 'analysisPending'
        : generation.state === 'unavailable' || generation.state === 'failed'
          ? 'analysisFailed'
          : null

  return {
    state,
    status,
    // Running long is retryable by definition (x-polling). An unreadable status
    // is not — see `Stopped`. Otherwise the Server decides, and a deploy with
    // no key configured is never retryable.
    retryable: stopped === 'timeout' || (stopped === null && (generation?.retryable ?? false)),
    retry,
  }
}

/**
 * The one line every prose screen uses. Starts from the state on the payload,
 * and polls ONLY while that says `pending` — x-polling forbids polling in
 * ready, in analysisFailed, and when generation is unavailable, which is
 * exactly the set this excludes.
 *
 * `onGenerated` refetches the payload, which is what actually brings prose to
 * the screen; the status endpoint only says when to bother.
 */
export function useProseState(
  unit: UnitState | undefined | null,
  onGenerated?: () => void,
): AnalysisGate {
  const fromPayload = unitAnalysisState(unit)
  const gate = useAnalysisStatus({ enabled: fromPayload === 'analysisPending', onGenerated })

  if (fromPayload !== 'analysisPending') return { ...gate, state: fromPayload }
  // Still pending as far as the payload knows; the poll may already know better.
  return { ...gate, state: gate.state ?? 'analysisPending' }
}
